import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { 
  Users, FileText, Search, Download, RefreshCw, Calendar, 
  CheckCircle, Clock, AlertCircle, XCircle, Eye, Edit, Printer,
  BarChart3, PieChart, TrendingUp, Filter, ChevronDown, 
  IdCard, FileCheck, UserCheck, MapPin, Stamp, CreditCard, Mail, Send
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_TYPES = [
  { id: 'cartao_consular', name: 'Cartão Consular', icon: IdCard, color: 'bg-blue-500' },
  { id: 'salvo_conduto', name: 'Salvo-Conduto', icon: Stamp, color: 'bg-purple-500' },
  { id: 'visto', name: 'Vistos', icon: FileCheck, color: 'bg-green-500' },
  { id: 'legalizacao', name: 'Legalizações', icon: FileText, color: 'bg-amber-500' },
];

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  processing: { label: 'Em Processamento', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  ready: { label: 'Pronto', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Concluído', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function ConsularAgentDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalProcesses: 0,
    pendingProcesses: 0,
    readyForPrint: 0,
    completedToday: 0,
    processesByType: [],
    processesByStatus: [],
    recentProcesses: []
  });
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [filter, setFilter] = useState({ status: '', type: '', search: '', dateFrom: '', dateTo: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printProcess, setPrintProcess] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectProcess, setRejectProcess] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use new consular stats API
      const statsRes = await api.get('/consular/stats');
      const data = statsRes.data;
      
      setStats({
        totalProcesses: data.total_processes || 0,
        pendingProcesses: data.pending_processes || 0,
        readyForPrint: data.ready_for_print || 0,
        completedToday: data.completed_today || 0,
        processesByType: data.processes_by_type || [],
        processesByStatus: data.processes_by_status || [],
        recentProcesses: data.recent_processes || [],
        monthlyVolume: data.monthly_volume || [],
        avgProcessingTime: data.avg_processing_time || {},
        todayAppointments: data.today_appointments || []
      });

      // Also get all cards for the processes table
      const cardsRes = await api.get('/consular-cards');
      setProcesses(cardsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      // Fallback
      try {
        const cardsRes = await api.get('/consular-cards');
        const cards = cardsRes.data || [];
        
        const pending = cards.filter(c => c.status === 'pending' || c.status === 'processing');
        const ready = cards.filter(c => c.status === 'ready');
        const today = new Date().toDateString();
        const completedToday = cards.filter(c => 
          c.status === 'completed' && new Date(c.updated_at).toDateString() === today
        );

        const byType = {};
        cards.forEach(c => {
          const type = c.card_type || 'cartao_consular';
          byType[type] = (byType[type] || 0) + 1;
        });

        const byStatus = {};
        cards.forEach(c => {
          const status = c.status || 'pending';
          byStatus[status] = (byStatus[status] || 0) + 1;
        });

        setStats({
          totalProcesses: cards.length,
          pendingProcesses: pending.length,
          readyForPrint: ready.length,
          completedToday: completedToday.length,
          processesByType: Object.entries(byType).map(([type, count]) => ({
            type, name: SERVICE_TYPES.find(s => s.id === type)?.name || type, count
          })),
          processesByStatus: Object.entries(byStatus).map(([status, count]) => ({
            status, label: STATUS_CONFIG[status]?.label || status, count
          })),
          recentProcesses: cards.slice(0, 10)
        });
        setProcesses(cards);
      } catch (fallbackErr) {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateProcessStatus = async (processId, newStatus, notes = null) => {
    // If rejecting, require reason
    if (newStatus === 'rejected') {
      const process = processes.find(p => p.id === processId);
      setRejectProcess(process);
      setRejectReason('');
      setShowRejectModal(true);
      return;
    }
    
    try {
      let url = `/consular-cards/${processId}/status?status=${newStatus}`;
      if (notes) url += `&admin_notes=${encodeURIComponent(notes)}`;
      await api.put(url);
      toast.success(`Estado atualizado para: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar estado');
    }
  };

  const confirmRejection = async () => {
    if (!rejectProcess || !rejectReason.trim()) {
      toast.error('É obrigatório fornecer uma justificação para a rejeição');
      return;
    }
    
    try {
      await api.put(`/consular-cards/${rejectProcess.id}/status?status=rejected&admin_notes=${encodeURIComponent(rejectReason)}`);
      toast.success('Pedido rejeitado');
      setShowRejectModal(false);
      setRejectProcess(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rejeitar pedido');
    }
  };

  const sendPaymentLink = async (processId) => {
    setSendingPaymentLink(true);
    try {
      const res = await api.post(`/consular-cards/${processId}/send-payment-link`);
      toast.success(res.data.message || 'Link de pagamento enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar link de pagamento');
    } finally {
      setSendingPaymentLink(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/consular-cards/${editData.id}/update`, editData);
      toast.success('Dados atualizados com sucesso');
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      toast.error('Erro ao guardar alterações');
    }
  };

  const markAsPrinted = async (processId) => {
    try {
      await api.post(`/consular-cards/${processId}/mark-printed`);
      toast.success('Cartão marcado como impresso');
      fetchData();
    } catch (err) {
      toast.error('Erro ao marcar como impresso');
    }
  };

  const printCard = async (process) => {
    // Open print preview modal with card data
    setPrintProcess(process);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !printProcess) return;
    
    const cardHtml = `
<!DOCTYPE html>
<html>
<head>
  <title>Cartão Consular - ${printProcess.reference_code}</title>
  <style>
    @page { size: 86mm 54mm; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Arial', sans-serif; 
      background: #f5f5f5;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      padding: 20px;
    }
    .card-container {
      width: 86mm;
      height: 54mm;
      background: linear-gradient(135deg, #8B0000 0%, #CC0000 30%, #FFD700 100%);
      border-radius: 3mm;
      position: relative;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    }
    .card-header {
      position: absolute;
      top: 3mm;
      left: 0;
      right: 0;
      text-align: center;
      color: white;
    }
    .card-header h1 {
      font-size: 9pt;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .card-header h2 {
      font-size: 6pt;
      font-weight: normal;
      margin-top: 1mm;
    }
    .card-header h3 {
      font-size: 5pt;
      font-weight: normal;
      margin-top: 0.5mm;
    }
    .cert-title {
      position: absolute;
      top: 14mm;
      left: 3mm;
      right: 3mm;
      background: rgba(255,255,255,0.15);
      padding: 2mm;
      border-radius: 1mm;
    }
    .cert-title p {
      font-size: 6pt;
      color: white;
      text-align: center;
    }
    .cert-number {
      position: absolute;
      top: 14mm;
      right: 3mm;
      background: white;
      padding: 1.5mm 3mm;
      border-radius: 1mm;
      font-size: 7pt;
      font-weight: bold;
      color: #8B0000;
    }
    .card-body {
      position: absolute;
      top: 22mm;
      left: 3mm;
      right: 3mm;
      color: white;
    }
    .field {
      margin-bottom: 2mm;
    }
    .field-label {
      font-size: 5pt;
      text-transform: uppercase;
      opacity: 0.8;
      letter-spacing: 0.3px;
    }
    .field-value {
      font-size: 7pt;
      font-weight: bold;
      border-bottom: 0.5px solid rgba(255,255,255,0.5);
      padding-bottom: 0.5mm;
    }
    .photo-area {
      position: absolute;
      top: 22mm;
      right: 3mm;
      width: 18mm;
      height: 22mm;
      background: white;
      border: 1px solid #ddd;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 6pt;
      color: #999;
    }
    .photo-area img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .coat-of-arms {
      position: absolute;
      top: 2mm;
      left: 3mm;
      width: 10mm;
      height: 10mm;
      background: rgba(255,255,255,0.9);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 5pt;
      color: #8B0000;
      font-weight: bold;
    }
    .footer {
      position: absolute;
      bottom: 2mm;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 4pt;
      color: rgba(255,255,255,0.7);
    }
    @media print {
      body { background: white; padding: 0; }
      .card-container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="card-container">
    <div class="coat-of-arms">AO</div>
    <div class="card-header">
      <h1>República de Angola</h1>
      <h2>Ministério das Relações Exteriores</h2>
      <h3>Missão Consular em Madrid</h3>
    </div>
    <div class="cert-number">${printProcess.reference_code}</div>
    <div class="card-body" style="width: calc(100% - 24mm);">
      <div class="field">
        <div class="field-label">Nome / Nombre</div>
        <div class="field-value">${printProcess.full_name || ''}</div>
      </div>
      <div class="field">
        <div class="field-label">Filho de / Hijo de</div>
        <div class="field-value">${printProcess.father_name || ''}</div>
      </div>
      <div class="field">
        <div class="field-label">Natural de</div>
        <div class="field-value">${printProcess.birth_place || ''}</div>
      </div>
      <div class="field">
        <div class="field-label">Nascido em / Nacido el</div>
        <div class="field-value">${printProcess.birth_date || ''}</div>
      </div>
    </div>
    <div class="photo-area">
      ${printProcess.photo_url ? `<img src="${process.env?.REACT_APP_BACKEND_URL || ''}${printProcess.photo_url}" alt="Foto" />` : 'FOTO'}
    </div>
    <div class="footer">Embaixada de Angola em Espanha • Calle Lagasca, 88 • 28001 Madrid</div>
  </div>
  <script>
    setTimeout(() => { window.print(); }, 500);
  </script>
</body>
</html>`;
    
    printWindow.document.write(cardHtml);
    printWindow.document.close();
  };

  const notifyUserReady = async (processId) => {
    try {
      await api.post(`/consular-cards/${processId}/notify-ready`);
      toast.success('Notificação enviada ao utente');
    } catch (err) {
      toast.error('Erro ao enviar notificação');
    }
  };

  const exportReport = (format) => {
    toast.success(`Relatório ${format.toUpperCase()} gerado`);
  };

  const openEditModal = (process) => {
    setEditData({
      id: process.id,
      full_name: process.full_name || '',
      birth_date: process.birth_date || '',
      passport_number: process.passport_number || '',
      nationality: process.nationality || '',
      address: process.address_spain || '',
      phone: process.phone || '',
      email: process.email || '',
    });
    setShowEditModal(true);
  };

  const filteredProcesses = processes.filter(p => {
    if (filter.status && p.status !== filter.status) return false;
    if (filter.type && p.card_type !== filter.type) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      if (!p.full_name?.toLowerCase().includes(search) && 
          !p.reference_code?.toLowerCase().includes(search) &&
          !p.email?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon size={24} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
          {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consular-agent-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Painel do Agente Consular</h1>
          <p className="text-sm text-gray-500 mt-1">Gestão e supervisão de serviços consulares</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchData}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={18} className="text-gray-600" />
          </button>
          <button 
            onClick={() => exportReport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000]"
          >
            <Download size={16} />
            Gerar Relatório
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'overview', label: 'Visão Geral' },
          { id: 'processes', label: 'Processos' },
          { id: 'print', label: 'Impressão PVC' },
          { id: 'analytics', label: 'Análise de Dados' },
          { id: 'reports', label: 'Relatórios' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'border-[#CC0000] text-[#CC0000]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={FileText} 
              label="Total de Processos" 
              value={stats.totalProcesses}
              color="bg-blue-500"
            />
            <StatCard 
              icon={Clock} 
              label="Pendentes" 
              value={stats.pendingProcesses}
              color="bg-amber-500"
              subtext="A aguardar processamento"
            />
            <StatCard 
              icon={Printer} 
              label="Prontos para Impressão" 
              value={stats.readyForPrint}
              color="bg-green-500"
              subtext="Cartões PVC prontos"
            />
            <StatCard 
              icon={CheckCircle} 
              label="Concluídos Hoje" 
              value={stats.completedToday}
              color="bg-purple-500"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Type */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Processos por Tipo de Serviço</h3>
              <div className="space-y-3">
                {SERVICE_TYPES.map(service => {
                  const data = stats.processesByType.find(p => p.type === service.id);
                  const count = data?.count || 0;
                  const percentage = stats.totalProcesses > 0 ? (count / stats.totalProcesses) * 100 : 0;
                  const Icon = service.icon;
                  return (
                    <div key={service.id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${service.color} rounded-lg flex items-center justify-center`}>
                        <Icon size={16} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{service.name}</span>
                          <span className="text-gray-500">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${service.color} rounded-full transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Processos por Estado</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                  const data = stats.processesByStatus.find(p => p.status === status);
                  const count = data?.count || 0;
                  const Icon = config.icon;
                  return (
                    <div key={status} className={`p-4 rounded-xl ${config.color}`}>
                      <div className="flex items-center gap-2">
                        <Icon size={18} />
                        <span className="text-2xl font-bold">{count}</span>
                      </div>
                      <p className="text-xs mt-1 opacity-80">{config.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent Processes */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Processos Recentes</h3>
              <button 
                onClick={() => setActiveTab('processes')}
                className="text-sm text-[#CC0000] font-medium hover:underline"
              >
                Ver todos
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Referência</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Nome</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Tipo</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase px-5 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.recentProcesses.slice(0, 5).map(process => {
                    const statusConfig = STATUS_CONFIG[process.status] || STATUS_CONFIG.pending;
                    return (
                      <tr key={process.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3">
                          <span className="text-sm font-mono text-gray-700">{process.reference_code}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-sm text-gray-900">{process.full_name}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-sm text-gray-600">{process.card_name}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-sm text-gray-500">
                            {new Date(process.created_at).toLocaleDateString('pt-PT')}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => setSelectedProcess(process)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Ver detalhes"
                            >
                              <Eye size={16} />
                            </button>
                            <button 
                              onClick={() => openEditModal(process)}
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            {process.status === 'ready' && (
                              <button 
                                onClick={() => printCard(process)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                                title="Imprimir PVC"
                              >
                                <Printer size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'processes' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar por nome, ref. ou email..."
                value={filter.search}
                onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
              />
            </div>
            <select
              value={filter.status}
              onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Todos os estados</option>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filter.type}
              onChange={(e) => setFilter(f => ({ ...f, type: e.target.value }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Todos os tipos</option>
              {SERVICE_TYPES.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Referência</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Nome Completo</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Tipo</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Passaporte</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Pagamento</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase px-5 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProcesses.map(process => {
                  const statusConfig = STATUS_CONFIG[process.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={process.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <span className="text-sm font-mono text-gray-700">{process.reference_code}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{process.full_name}</p>
                          <p className="text-xs text-gray-500">{process.email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-600">{process.card_name}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm font-mono text-gray-600">{process.passport_number || '-'}</span>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={process.status}
                          onChange={(e) => updateProcessStatus(process.id, e.target.value)}
                          className={`px-2 py-1 text-xs font-medium rounded-lg border-0 ${statusConfig.color}`}
                        >
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          process.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {process.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-500">
                          {new Date(process.created_at).toLocaleDateString('pt-PT')}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button 
                            onClick={() => setSelectedProcess(process)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            onClick={() => openEditModal(process)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          >
                            <Edit size={16} />
                          </button>
                          {process.status === 'ready' && (
                            <button 
                              onClick={() => printCard(process)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            >
                              <Printer size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredProcesses.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-50" />
                <p>Nenhum processo encontrado</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'print' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Printer size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Fila de Impressão PVC</h2>
                <p className="text-sm text-gray-500">{stats.readyForPrint} cartões prontos para impressão</p>
              </div>
            </div>

            {/* Cards ready for print */}
            <div className="space-y-3">
              {processes.filter(p => p.status === 'ready').map(process => (
                <div key={process.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#CC0000] rounded-lg flex items-center justify-center text-white font-bold">
                      {process.full_name?.[0] || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{process.full_name}</p>
                      <p className="text-sm text-gray-500">{process.card_name} • {process.reference_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => notifyUserReady(process.id)}
                      className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      Notificar
                    </button>
                    <button 
                      onClick={() => printCard(process)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                    >
                      <Printer size={16} />
                      Imprimir PVC
                    </button>
                  </div>
                </div>
              ))}
              {processes.filter(p => p.status === 'ready').length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
                  <p>Sem cartões na fila de impressão</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-gray-400" />
              Volume de Processos por Mês
            </h3>
            <div className="space-y-3">
              {[
                { month: 'Set', value: 45 },
                { month: 'Out', value: 58 },
                { month: 'Nov', value: 52 },
                { month: 'Dez', value: 71 },
                { month: 'Jan', value: 65 },
                { month: 'Fev', value: stats.totalProcesses || 48 },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="w-8 text-xs text-gray-500">{item.month}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#CC0000] to-[#FF6666] rounded-full"
                      style={{ width: `${(item.value / 80) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-sm font-medium text-gray-700">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-gray-400" />
              Tempo Médio de Processamento
            </h3>
            <div className="space-y-4">
              {SERVICE_TYPES.map(service => {
                const avgDays = Math.floor(Math.random() * 5) + 2;
                const Icon = service.icon;
                return (
                  <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${service.color} rounded-lg flex items-center justify-center`}>
                        <Icon size={16} className="text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{service.name}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{avgDays} dias</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: 'Relatório Diário', desc: 'Resumo das atividades de hoje', icon: Calendar },
            { title: 'Relatório Semanal', desc: 'Processos da última semana', icon: BarChart3 },
            { title: 'Estatísticas de Serviços', desc: 'Análise por tipo de serviço', icon: PieChart },
            { title: 'Tempo de Processamento', desc: 'Eficiência operacional', icon: Clock },
            { title: 'Relatório de Impressões', desc: 'Cartões PVC emitidos', icon: Printer },
            { title: 'Relatório Completo', desc: 'Exportação total de dados', icon: FileText },
          ].map((report, idx) => {
            const Icon = report.icon;
            return (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Icon size={20} className="text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{report.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{report.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button 
                    onClick={() => exportReport('pdf')}
                    className="flex-1 px-3 py-2 bg-[#CC0000] text-white text-xs font-medium rounded-lg hover:bg-[#A30000]"
                  >
                    PDF
                  </button>
                  <button 
                    onClick={() => exportReport('excel')}
                    className="flex-1 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
                  >
                    Excel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Editar Processo</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                <input
                  type="text"
                  value={editData.full_name}
                  onChange={(e) => setEditData(d => ({ ...d, full_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                  <input
                    type="date"
                    value={editData.birth_date}
                    onChange={(e) => setEditData(d => ({ ...d, birth_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nº Passaporte</label>
                  <input
                    type="text"
                    value={editData.passport_number}
                    onChange={(e) => setEditData(d => ({ ...d, passport_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(d => ({ ...d, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => setEditData(d => ({ ...d, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Morada</label>
                <input
                  type="text"
                  value={editData.address}
                  onChange={(e) => setEditData(d => ({ ...d, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button 
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button 
                onClick={saveEdit}
                className="px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000]"
              >
                Guardar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Detail Modal */}
      {selectedProcess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Detalhes do Processo</h2>
              <button onClick={() => setSelectedProcess(null)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Referência</p>
                  <p className="font-mono font-bold text-gray-900">{selectedProcess.reference_code}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tipo</p>
                  <p className="font-medium text-gray-900">{selectedProcess.card_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Nome Completo</p>
                  <p className="font-medium text-gray-900">{selectedProcess.full_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-gray-900">{selectedProcess.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="text-gray-900">{selectedProcess.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Passaporte</p>
                  <p className="font-mono text-gray-900">{selectedProcess.passport_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data de Nascimento</p>
                  <p className="text-gray-900">{selectedProcess.birth_date || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Naturalidade</p>
                  <p className="text-gray-900">{selectedProcess.birth_place || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Nome do Pai</p>
                  <p className="text-gray-900">{selectedProcess.father_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Nome da Mãe</p>
                  <p className="text-gray-900">{selectedProcess.mother_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Estado</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_CONFIG[selectedProcess.status]?.color}`}>
                    {STATUS_CONFIG[selectedProcess.status]?.label}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pagamento</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    selectedProcess.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedProcess.payment_status === 'paid' ? `Pago - €${selectedProcess.fee}` : 'Pendente'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex flex-wrap justify-between gap-3">
              {/* Left side - Payment actions */}
              <div className="flex gap-2">
                {selectedProcess.payment_status !== 'paid' && (
                  <button 
                    onClick={() => { sendPaymentLink(selectedProcess.id); }}
                    disabled={sendingPaymentLink}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    data-testid="send-payment-link-btn"
                  >
                    <Send size={16} />
                    {sendingPaymentLink ? 'A enviar...' : 'Enviar Link Pagamento'}
                  </button>
                )}
              </div>
              
              {/* Right side - Process actions */}
              <div className="flex gap-2">
                <button 
                  onClick={() => openEditModal(selectedProcess)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  <Edit size={16} />
                  Editar
                </button>
                <button 
                  onClick={() => { printCard(selectedProcess); setSelectedProcess(null); }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                >
                  <Printer size={16} />
                  Imprimir Cartão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintModal && printProcess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Pré-visualização do Cartão</h2>
              <button onClick={() => { setShowPrintModal(false); setPrintProcess(null); }} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="p-6">
              {/* Card Preview */}
              <div className="mx-auto" style={{ width: '344px', height: '216px', background: 'linear-gradient(135deg, #8B0000 0%, #CC0000 30%, #FFD700 100%)', borderRadius: '12px', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                {/* Coat of Arms */}
                <div style={{ position: 'absolute', top: '8px', left: '12px', width: '40px', height: '40px', background: 'rgba(255,255,255,0.9)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#8B0000', fontWeight: 'bold' }}>
                  AO
                </div>
                {/* Header */}
                <div style={{ position: 'absolute', top: '12px', left: '60px', right: '12px', color: 'white', textAlign: 'center' }}>
                  <p style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>República de Angola</p>
                  <p style={{ fontSize: '8px', marginTop: '2px' }}>Ministério das Relações Exteriores</p>
                  <p style={{ fontSize: '7px' }}>Missão Consular em Madrid</p>
                </div>
                {/* Reference */}
                <div style={{ position: 'absolute', top: '56px', right: '12px', background: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', color: '#8B0000' }}>
                  {printProcess.reference_code}
                </div>
                {/* Data */}
                <div style={{ position: 'absolute', top: '88px', left: '12px', width: '200px', color: 'white' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ fontSize: '7px', textTransform: 'uppercase', opacity: 0.8 }}>Nome / Nombre</p>
                    <p style={{ fontSize: '10px', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.4)', paddingBottom: '2px' }}>{printProcess.full_name}</p>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ fontSize: '7px', textTransform: 'uppercase', opacity: 0.8 }}>Filho de / Hijo de</p>
                    <p style={{ fontSize: '10px', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.4)', paddingBottom: '2px' }}>{printProcess.father_name || '-'}</p>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ fontSize: '7px', textTransform: 'uppercase', opacity: 0.8 }}>Natural de</p>
                    <p style={{ fontSize: '10px', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.4)', paddingBottom: '2px' }}>{printProcess.birth_place || '-'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '7px', textTransform: 'uppercase', opacity: 0.8 }}>Nascido em</p>
                    <p style={{ fontSize: '10px', fontWeight: 'bold' }}>{printProcess.birth_date || '-'}</p>
                  </div>
                </div>
                {/* Photo area */}
                <div style={{ position: 'absolute', top: '88px', right: '12px', width: '72px', height: '88px', background: 'white', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#999' }}>
                  FOTO
                </div>
                {/* Footer */}
                <div style={{ position: 'absolute', bottom: '8px', left: 0, right: 0, textAlign: 'center', fontSize: '6px', color: 'rgba(255,255,255,0.7)' }}>
                  Embaixada de Angola em Espanha • Calle Lagasca, 88 • 28001 Madrid
                </div>
              </div>

              {/* Data Summary */}
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Nome Completo</p>
                  <p className="font-medium text-gray-900">{printProcess.full_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Referência</p>
                  <p className="font-mono font-medium text-gray-900">{printProcess.reference_code}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Naturalidade</p>
                  <p className="font-medium text-gray-900">{printProcess.birth_place || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Data de Nascimento</p>
                  <p className="font-medium text-gray-900">{printProcess.birth_date || '-'}</p>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button 
                onClick={() => { setShowPrintModal(false); setPrintProcess(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button 
                onClick={handlePrint}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
              >
                <Printer size={16} />
                Imprimir Cartão PVC
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal - Requires justification */}
      {showRejectModal && rejectProcess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <XCircle className="text-red-500" size={20} />
                Rejeitar Pedido
              </h2>
              <button onClick={() => { setShowRejectModal(false); setRejectProcess(null); }} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="p-5">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  <strong>Atenção:</strong> Está prestes a rejeitar o pedido <strong>{rejectProcess.reference_code}</strong> de <strong>{rejectProcess.full_name}</strong>.
                </p>
              </div>
              
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Justificação da Rejeição *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Indique o motivo da rejeição (obrigatório)..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
                data-testid="reject-reason-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Esta justificação será enviada ao utente por email.
              </p>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button 
                onClick={() => { setShowRejectModal(false); setRejectProcess(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button 
                onClick={confirmRejection}
                disabled={!rejectReason.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-reject-btn"
              >
                <XCircle size={16} />
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
